import React, { useEffect, useState } from 'react'
import {
  Search,
  Plus,
  Filter,
  Edit2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Package,
  X,
  Heart,
  MessageCircle,
  Share2,
  ChevronDown
} from 'lucide-react'
import { Card, StatusBadge } from '../ui'
import Tooltip from '../ui/Tooltip'
import useAssetStore from '../../stores/assetStore'
import { useAssetCategoryStore } from '../../stores/assetCategoryStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { useToast } from '../../contexts/ToastContext'
import AddAssetDrawer from './AddAssetDrawer'
import EditAssetDrawer from './EditAssetDrawer'
import AssetDetailDrawer from './AssetDetailDrawer'
import { Asset } from '../../types/asset'

const AssetsList: React.FC = () => {
  const { showError } = useToast()
  const { assets, pagination, loading, error, loadAssets } = useAssetStore()
  const { categories, loadCategories } = useAssetCategoryStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortBy, setSortBy] = useState<string>('total_emv')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Drawer states
  const [isAddAssetDrawerOpen, setIsAddAssetDrawerOpen] = useState(false)
  const [isEditAssetDrawerOpen, setIsEditAssetDrawerOpen] = useState(false)
  const [isDetailAssetDrawerOpen, setIsDetailAssetDrawerOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(1)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Load assets
  useEffect(() => {
    if (!selectedCustomer?.id) return

    loadAssets(
      String(selectedCustomer.id),
      statusFilter !== 'all' ? (statusFilter as any) : undefined,
      categoryFilter !== 'all' ? categoryFilter : undefined,
      undefined,
      debouncedSearch || undefined,
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder
    )
  }, [
    loadAssets,
    selectedCustomer,
    debouncedSearch,
    statusFilter,
    categoryFilter,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder
  ])

  // Load categories for filters
  useEffect(() => {
    if (selectedCustomer?.id) {
      loadCategories(String(selectedCustomer.id), 'Active')
    }
  }, [selectedCustomer?.id, loadCategories])

  useEffect(() => {
    if (error) {
      showError('Error', error)
    }
  }, [error, showError])

  const handleEditAsset = (asset: Asset) => {
    setSelectedAsset(asset)
    setIsEditAssetDrawerOpen(true)
  }

  const handleViewAsset = (asset: Asset) => {
    setSelectedAsset(asset)
    setIsDetailAssetDrawerOpen(true)
  }

  const handleAssetSaved = () => {
    if (!selectedCustomer?.id) return
    loadAssets(
      String(selectedCustomer.id),
      statusFilter !== 'all' ? (statusFilter as any) : undefined,
      categoryFilter !== 'all' ? categoryFilter : undefined,
      undefined,
      debouncedSearch || undefined,
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder
    )
  }

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  // Get row background color based on EMV (matches Attribution Tags table)
  const getRowBgClass = (asset: Asset) => {
    const emvValue = asset.total_emv || 0
    if (emvValue >= 500) {
      return 'bg-green-50 hover:bg-green-100'
    }
    if (emvValue >= 200) {
      return 'bg-yellow-50 hover:bg-yellow-100'
    }
    return 'bg-red-50 hover:bg-red-100'
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-bold text-gray-900'>Assets</h2>
          <p className='text-sm text-gray-600'>
            Manage recurring content formats and track performance
          </p>
        </div>
        <button
          onClick={() => setIsAddAssetDrawerOpen(true)}
          className='inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        >
          <Plus className='h-3.5 w-3.5 mr-1.5' />
          Add Asset
        </button>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg'>
        <div className='flex flex-wrap items-center gap-2'>
          {/* Search */}
          <div className='relative'>
            <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
            <input
              type='text'
              placeholder='Search assets...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64'
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className='absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className='relative'>
            <Filter className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className='pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white'
            >
              <option value='all'>All Status</option>
              <option value='Draft'>Draft</option>
              <option value='Active'>Active</option>
              <option value='Paused'>Paused</option>
              <option value='Retired'>Retired</option>
            </select>
          </div>

          {/* Post Format Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className='px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white'
          >
            <option value='all'>All Post Formats</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Assets Table */}
      <Card>
        {loading ? (
          <div className='text-center py-12'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            <p className='text-gray-500 mt-2'>Loading assets...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className='text-center py-12'>
            <Package className='h-12 w-12 mx-auto text-gray-300 mb-4' />
            <p className='text-gray-500'>
              {searchQuery ||
              statusFilter !== 'all' ||
              categoryFilter !== 'all'
                ? 'No assets match your filters'
                : 'No assets found. Add your first asset to get started.'}
            </p>
          </div>
        ) : (
          <>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-gray-200'>
                    <th
                      className='px-3 py-2 text-left cursor-pointer hover:bg-gray-50'
                      onClick={() => handleSort('name')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                          Asset Name
                        </span>
                        <ChevronDown
                          className={`h-3 w-3 text-gray-400 transition-transform ${
                            sortBy === 'name'
                              ? sortOrder === 'desc'
                                ? 'rotate-180'
                                : ''
                              : 'opacity-0'
                          }`}
                        />
                      </div>
                    </th>
                    <th
                      className='px-3 py-2 text-center cursor-pointer hover:bg-gray-50'
                      onClick={() => handleSort('account_count')}
                    >
                      <div className='flex items-center justify-center space-x-1'>
                        <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                          Accounts
                        </span>
                        <ChevronDown
                          className={`h-3 w-3 text-gray-400 transition-transform ${
                            sortBy === 'account_count'
                              ? sortOrder === 'desc'
                                ? 'rotate-180'
                                : ''
                              : 'opacity-0'
                          }`}
                        />
                      </div>
                    </th>
                    <th
                      className='px-3 py-2 text-center cursor-pointer hover:bg-gray-50'
                      onClick={() => handleSort('hashtag_count')}
                    >
                      <div className='flex items-center justify-center space-x-1'>
                        <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                          Tags
                        </span>
                        <ChevronDown
                          className={`h-3 w-3 text-gray-400 transition-transform ${
                            sortBy === 'hashtag_count'
                              ? sortOrder === 'desc'
                                ? 'rotate-180'
                                : ''
                              : 'opacity-0'
                          }`}
                        />
                      </div>
                    </th>
                    <th className='px-3 py-2 text-left'>
                      <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                        Post Format
                      </span>
                    </th>
                    <th className='px-3 py-2 text-left'>
                      <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                        Engagement
                      </span>
                    </th>
                    <th
                      className='px-3 py-2 text-right cursor-pointer hover:bg-gray-50'
                      onClick={() => handleSort('total_emv')}
                    >
                      <div className='flex items-center justify-end space-x-1'>
                        <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                          Total EMV
                        </span>
                        <ChevronDown
                          className={`h-3 w-3 text-gray-400 transition-transform ${
                            sortBy === 'total_emv'
                              ? sortOrder === 'desc'
                                ? 'rotate-180'
                                : ''
                              : 'opacity-0'
                          }`}
                        />
                      </div>
                    </th>
                    <th className='px-3 py-2 text-left'>
                      <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                        Status
                      </span>
                    </th>
                    <th className='px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {assets.map((asset) => (
                    <tr
                      key={asset.id}
                      className={`cursor-pointer transition-colors ${getRowBgClass(asset)}`}
                      onClick={() => handleViewAsset(asset)}
                    >
                      <td className='px-3 py-2'>
                        <div>
                          <p className='text-xs font-medium text-gray-900'>
                            {asset.name}
                          </p>
                          {asset.description && (
                            <p className='text-[10px] text-gray-500 mt-0.5 line-clamp-1'>
                              {asset.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className='px-3 py-2 text-center'>
                        <span className='text-xs text-gray-700'>
                          {Number(asset.account_count) || 0}
                        </span>
                      </td>
                      <td className='px-3 py-2 text-center'>
                        <span className='text-xs text-gray-700'>
                          {Number(asset.hashtag_count) || 0}
                        </span>
                      </td>
                      <td className='px-3 py-2'>
                        {asset.category_names && asset.category_names.length > 0 ? (
                          <div className='flex flex-wrap gap-1'>
                            {asset.category_names.slice(0, 2).map((name, idx) => (
                              <span
                                key={idx}
                                className='text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded'
                              >
                                {name}
                              </span>
                            ))}
                            {asset.category_names.length > 2 && (
                              <span className='text-xs text-gray-500'>
                                +{asset.category_names.length - 2} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className='text-xs text-gray-400'>-</span>
                        )}
                      </td>
                      <td className='px-3 py-2'>
                        {asset.total_likes != null ||
                        asset.total_comments != null ||
                        asset.total_shares != null ||
                        asset.total_views != null ? (
                          <div className='flex items-center space-x-2 text-xs text-gray-500'>
                            <div className='flex items-center'>
                              <Heart className='h-3 w-3 mr-0.5' />
                              {formatNumber(asset.total_likes || 0)}
                            </div>
                            <div className='flex items-center'>
                              <MessageCircle className='h-3 w-3 mr-0.5' />
                              {formatNumber(asset.total_comments || 0)}
                            </div>
                            {(asset.total_shares || 0) > 0 && (
                              <div className='flex items-center'>
                                <Share2 className='h-3 w-3 mr-0.5' />
                                {formatNumber(asset.total_shares || 0)}
                              </div>
                            )}
                            <div className='flex items-center'>
                              <Eye className='h-3 w-3 mr-0.5' />
                              {formatNumber(asset.total_views || 0)}
                            </div>
                          </div>
                        ) : (
                          <span className='text-xs text-gray-400'>-</span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <span className='text-xs font-medium text-gray-700'>
                          {asset.total_emv != null
                            ? `$${asset.total_emv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '-'}
                        </span>
                      </td>
                      <td className='px-3 py-2'>
                        <StatusBadge status={asset.status} />
                      </td>
                      <td className='px-3 py-2'>
                        <div className='flex items-center justify-end gap-1'>
                          <Tooltip content='View Details'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewAsset(asset)
                              }}
                              className='p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors'
                            >
                              <Eye className='h-3.5 w-3.5' />
                            </button>
                          </Tooltip>
                          <Tooltip content='Edit Asset'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditAsset(asset)
                              }}
                              className='p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors'
                            >
                              <Edit2 className='h-3.5 w-3.5' />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className='border-t border-gray-200 px-4 py-3 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span className='text-xs text-gray-600'>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(
                      currentPage * itemsPerPage,
                      pagination.totalAssets
                    )}{' '}
                    of {pagination.totalAssets} assets
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    className='px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>

                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.hasPrevPage}
                    className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  <span className='px-3 py-1 text-xs text-gray-600'>
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={!pagination.hasNextPage}
                    className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Drawers */}
      <AddAssetDrawer
        isOpen={isAddAssetDrawerOpen}
        onClose={() => setIsAddAssetDrawerOpen(false)}
        onSave={handleAssetSaved}
      />
      <EditAssetDrawer
        isOpen={isEditAssetDrawerOpen}
        onClose={() => {
          setIsEditAssetDrawerOpen(false)
          setSelectedAsset(null)
        }}
        asset={selectedAsset}
        onSave={handleAssetSaved}
      />
      <AssetDetailDrawer
        isOpen={isDetailAssetDrawerOpen}
        onClose={() => {
          setIsDetailAssetDrawerOpen(false)
          setSelectedAsset(null)
        }}
        asset={selectedAsset}
        onEditClick={() => {
          setIsDetailAssetDrawerOpen(false)
          setIsEditAssetDrawerOpen(true)
        }}
      />
    </div>
  )
}

export default AssetsList
